import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button, Layout } from "antd";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import {
  AiOutlineMenu,
  AiOutlineDashboard,
  AiOutlineShoppingCart,
  AiOutlineUser,
  AiOutlineBoxPlot,
  AiOutlineTag,
  AiOutlineLogout,
  AiOutlineMore,
  AiOutlineFileExcel,
  AiOutlinePlusSquare,
} from "react-icons/ai";
import { MdOutlineStorefront, MdCorporateFare } from "react-icons/md";
import { FaRegCircleUser } from "react-icons/fa6";
import { FiBookOpen } from "react-icons/fi";
import { GoCodeReview } from "react-icons/go";
import { RiCoupon5Line } from "react-icons/ri";
import { RiCoupon4Fill } from "react-icons/ri";
import { BiSolidCoupon } from "react-icons/bi";
const { Header, Content } = Layout;

const AdminPanel = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Session check logic
  useEffect(() => {
    const sessionItem = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
    if (!sessionItem) {
      setUser(null);
      return;
    }
    try {
      const decodedItem = atob(sessionItem);
      const parsedUser = JSON.parse(decodedItem);
      setUser(parsedUser);
    } catch (error) {
      sessionStorage.removeItem("H7Z4L9VD2XK5JW8QRYT6");
      setUser(null);
    }
  }, []);

  const handleLogoutConfirm = () => {
    sessionStorage.removeItem("H7Z4L9VD2XK5JW8QRYT6");
    toast.success("Logged out successfully");
    setTimeout(() => navigate("/admin-login"), 800);
  };

  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  const toggleSubmenu = (key) => {
    setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const mainMenuItems = [
    {
      key: "dashboard",
      icon: <AiOutlineDashboard />,
      label: "Dashboard",
      path: "/admin-panel/dashboard",
    },
    {
      key: "products",
      icon: <AiOutlineBoxPlot />,
      label: "Products",
      path: "/admin-panel/products",
    },
    {
      key: "products-package",
      icon: <AiOutlineBoxPlot />,
      label: "Products Package",
      path: "/admin-panel/products-package",
    },
    {
      key: "product-dimensions",
      icon: <AiOutlineBoxPlot />,
      label: "Product Dimensions",
      path: "/admin-panel/product-dimensions",
    },
    {
      key: "plans",
      icon: <FiBookOpen />,
      label: "Plans",
      path: "/admin-panel/plans",
    },
    
    {
      key: "packages",
      icon: <AiOutlineTag />,
      label: "Packages",
      path: "/admin-panel/packages",
    },
    {
      key: "sakhi",
      icon: <AiOutlineTag />,
      label: "Sakhi",
      path: "/admin-panel/sakhi",
    },
  ];

  const moreMenuItems = [
    {
      key: "customers",
      icon: <AiOutlineUser />,
      label: "Customers",
      children: [
        {
          key: "all-customers",
          icon: <AiOutlineUser />,
          label: "Customers",
          path: "/admin-panel/customers",
        },
        {
          key: "new-customer",
          icon: <AiOutlinePlusSquare />,
          label: "Organization Customers",
          path: "/admin-panel/organization-customers",
        },
      ],
      // { key: 'customers', icon: <AiOutlineUser />, label: 'Customers',
      //  path: '/admin-panel/customers'
    },
    {
      key: "Customer-reviews",
      icon: <GoCodeReview />,
      label: "Customer Reviews",
      path: "/admin-panel/customer-reviews",
    },
    {
      key: "orders",
      icon: <AiOutlineShoppingCart />,
      label: "Orders",
      path: "/admin-panel/orders",
    },

    {
      key: "organization",
      icon: <MdCorporateFare />,
      label: "Organization",
      children: [
        {
          key: "org-manage",
          icon: <AiOutlinePlusSquare />,
          label: "Manage / Create",
          path: "/admin-panel/organization",
        },
        {
          key: "org-upload",
          icon: <AiOutlineFileExcel />,
          label: "Upload Excel",
          path: "/admin-panel/upload-excel",
        },
      ],
    },
    {
      key: "coupon-management",
      icon: <RiCoupon5Line />,
      label: "Coupon Management",
      children: [
        {
          key: "coupon-create",
          icon: <RiCoupon4Fill />,
          label: "Create Coupon",
          path: "/admin-panel/coupon-create",
        },
        {
          key: "entry-coupons",
          icon: <BiSolidCoupon />,
          label: "Bulk Coupons",
          path: "/admin-panel/entry-coupons",
        },
      ],
    },
    {
      key: "Referral",
      icon: <MdOutlineStorefront />,
      label: "Referral",
      path: "/admin-panel/referrals",
    },
      {
      key: "password-reset",
      icon: <MdOutlineStorefront />,
      label: "Password Reset",
      path: "/admin-panel/admin-password-reset",
    },
    {
      key: "vendingmechine",
      icon: <MdOutlineStorefront />,
      label: "Vending",
      path: "/admin-panel/vendingmechine",
    },
  ];

  const handleMenuClick = (item) => {
    if (item.children) {
      toggleSubmenu(item.key);
    } else {
      navigate(item.path);
      setIsMoreOpen(false);
      window.scrollTo(0, 0);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <Layout className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 h-16 bg-pink-100 shadow-md border-b border-pink-200">
        <div className="flex items-center">
          <Button
            type="text"
            icon={<AiOutlineMenu className="text-pink-700 text-2xl" />}
            onClick={toggleSidebar}
            className="lg:block hidden"
          />
        </div>
        <Button
          icon={<AiOutlineLogout className="text-white mr-2" />}
          onClick={() => setShowLogoutModal(true)}
          className="bg-red-600 text-white hover:bg-red-700 border-none font-semibold flex items-center rounded-lg h-10"
        >
          Logout
        </Button>
      </Header>

      {/* Desktop Sidebar */}
      <div
        className={`hidden lg:block fixed left-0 top-16 bottom-0 bg-pink-100 shadow-2xl overflow-y-auto transition-all duration-300 border-r border-pink-200 z-40 pb-[20%] ${
          isSidebarCollapsed ? "w-20" : "w-64"
        }`}
      >
        <SidebarContent
          user={user}
          menuItems={[...mainMenuItems, ...moreMenuItems]}
          isActive={isActive}
          handleMenuClick={handleMenuClick}
          collapsed={isSidebarCollapsed}
          openSubmenus={openSubmenus}
        />
      </div>

      {/* Main Content Area */}
      <Layout
        className={`transition-all duration-300 pt-16 ${isSidebarCollapsed ? "lg:ml-20" : "lg:ml-64"}`}
      >
        <Content className="min-h-screen bg-gray-50 pb-20 lg:pb-0 p-4">
          <div className="bg-white shadow-xl border border-gray-100 min-h-full rounded-2xl overflow-hidden p-6">
            <Outlet />
          </div>
        </Content>
      </Layout>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-pink-200 shadow-2xl lg:hidden z-40">
        <nav className="grid grid-cols-5 gap-1 py-1 relative">
          {mainMenuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => handleMenuClick(item)}
              className={`flex flex-col items-center justify-center py-2 transition-all ${
                isActive(item.path)
                  ? "text-pink-600 bg-pink-50"
                  : "text-gray-500"
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="text-[10px] mt-1 font-bold uppercase">
                {item.label}
              </span>
            </button>
          ))}

          <div className="relative">
            <button
              onClick={() => setIsMoreOpen(!isMoreOpen)}
              className={`w-full flex flex-col items-center justify-center py-2 transition-all ${
                isMoreOpen ? "text-pink-600 bg-pink-50" : "text-gray-500"
              }`}
            >
              <span className="text-xl">
                <AiOutlineMore />
              </span>
              <span className="text-[10px] mt-1 font-bold uppercase">More</span>
            </button>

            {/* Mobile More Popover */}
            <AnimatePresence>
              {isMoreOpen && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-4 w-64 bg-white rounded-3xl shadow-2xl border border-pink-100 overflow-hidden"
                >
                  <div className="py-2 max-h-[75vh] overflow-y-auto">
                    {moreMenuItems.map((item) => (
                      <div key={item.key}>
                        <button
                          onClick={() => handleMenuClick(item)}
                          className={`w-full flex items-center justify-between px-6 py-4 ${
                            isActive(item.path)
                              ? "bg-pink-50 text-pink-600"
                              : "text-gray-700"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{item.icon}</span>
                            <span className="font-bold text-sm">
                              {item.label}
                            </span>
                          </div>
                          {item.children &&
                            (openSubmenus[item.key] ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            ))}
                        </button>

                        {/* Mobile Submenu Rendering */}
                        {item.children && openSubmenus[item.key] && (
                          <div className="bg-gray-50 py-1">
                            {item.children.map((child) => (
                              <button
                                key={child.key}
                                onClick={() => handleMenuClick(child)}
                                className={`w-full flex items-center gap-4 pl-14 pr-6 py-3 text-xs ${
                                  isActive(child.path)
                                    ? "text-pink-600 font-black"
                                    : "text-gray-500 font-bold"
                                }`}
                              >
                                {child.icon} {child.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>
      </div>

      {/* Logout Modal */}
      <AnimatePresence>
        {showLogoutModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-[2.5rem] p-10 w-full max-w-sm mx-4 shadow-2xl text-center"
            >
              <h3 className="text-2xl font-black text-gray-800 mb-2 uppercase tracking-tight">
                Logout
              </h3>
              <p className="text-gray-500 mb-8 font-medium italic">
                Are you sure you want to exit the admin portal?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 py-4 text-gray-400 font-bold hover:bg-gray-100 rounded-2xl transition"
                >
                  No, Stay
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="flex-1 py-4 bg-red-600 text-white font-black rounded-2xl hover:bg-red-700 transition shadow-lg shadow-red-100"
                >
                  Yes, Exit
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

// --- Sidebar Content Component ---
const SidebarContent = ({
  user,
  menuItems,
  isActive,
  handleMenuClick,
  collapsed,
  openSubmenus,
}) => (
  <div
    className={`py-8 flex flex-col h-full ${collapsed ? "items-center" : "px-4"}`}
  >
    {/* Admin Profile */}
    <div
      className={`flex flex-col items-center mb-8 pb-8 border-b border-pink-200 w-full`}
    >
      <div
        className={`rounded-full border-4 border-white shadow-xl flex items-center justify-center bg-pink-50 ${collapsed ? "w-12 h-12" : "w-20 h-20"}`}
      >
        <FaRegCircleUser
          className={`text-pink-600 ${collapsed ? "text-3xl" : "text-5xl"}`}
        />
      </div>
      {!collapsed && (
        <div className="text-center mt-4">
          <p className="text-pink-900 font-black text-lg uppercase tracking-tight leading-none">
            {user?.name || "ADMIN"}
          </p>
          <span className="text-pink-400 text-[10px] font-black uppercase tracking-widest mt-1 inline-block">
            Management Portal
          </span>
        </div>
      )}
    </div>

    {/* Nav Menu */}
    <nav className="flex-1 space-y-2 w-full">
      {menuItems.map((item) => (
        <div key={item.key}>
          <button
            onClick={() => handleMenuClick(item)}
            className={`w-full flex items-center rounded-2xl transition-all relative group ${
              collapsed ? "justify-center py-4" : "justify-between px-5 py-3.5"
            } ${isActive(item.path) && !item.children ? "bg-pink-500 text-white shadow-lg shadow-pink-200" : "text-gray-700 hover:bg-pink-50"}`}
          >
            <div className="flex items-center gap-4">
              <span
                className={`text-2xl ${isActive(item.path) && !item.children ? "text-white" : "text-pink-600"}`}
              >
                {item.icon}
              </span>
              {!collapsed && (
                <span className="font-bold text-sm uppercase tracking-tight">
                  {item.label}
                </span>
              )}
            </div>

            {!collapsed &&
              item.children &&
              (openSubmenus[item.key] ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              ))}
          </button>

          {/* Desktop Submenu (Expandable) */}
          {!collapsed && item.children && openSubmenus[item.key] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="ml-8 mt-2 space-y-1 overflow-hidden border-l-2 border-pink-200"
            >
              {item.children.map((child) => (
                <button
                  key={child.key}
                  onClick={() => handleMenuClick(child)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[11px] font-black uppercase transition-all ${
                    isActive(child.path)
                      ? "text-pink-600 bg-pink-50"
                      : "text-gray-400 hover:text-pink-500"
                  }`}
                >
                  {child.icon} {child.label}
                </button>
              ))}
            </motion.div>
          )}
        </div>
      ))}
    </nav>
  </div>
);

export default AdminPanel;
